const MAX_FILE_BYTES = 100 * 1024 * 1024 * 1024;
const MAX_DURATION_SEC = 12 * 3600;
const KNOWN_MEDIA_EXTENSIONS =
  /\.(mp3|mp4|m4a|wav|flac|ogg|webm|mov|mkv|aac|wma|aiff?|mpeg|mpg|avi|wmv|3gp|opus)$/i;

export { MAX_DURATION_SEC, MAX_FILE_BYTES };

export async function preflightMediaFile(file) {
  if (!file) {
    return { ok: false, reason: "invalid_type" };
  }

  if (!Number.isFinite(file.size) || file.size > MAX_FILE_BYTES) {
    return { ok: false, reason: "file_too_large" };
  }

  if (!isSupportedMediaFilename(file.name)) {
    return { ok: false, reason: "invalid_type" };
  }

  const durationSec = await readMediaDurationSec(file);
  if (!Number.isFinite(durationSec) || durationSec <= 0) {
    return { ok: false, reason: "duration_unreadable" };
  }
  if (durationSec > MAX_DURATION_SEC) {
    return { ok: false, reason: "duration_too_long" };
  }

  return { ok: true, durationSec };
}

function isSupportedMediaFilename(filename) {
  if (!filename || typeof filename !== "string") return false;
  return KNOWN_MEDIA_EXTENSIONS.test(filename);
}

async function readMediaDurationSec(file) {
  if (typeof document === "undefined") return null;

  const url = URL.createObjectURL(file);
  try {
    const tags = inferMediaTags(file);
    for (const tag of tags) {
      try {
        const duration = await readDurationWithElement(tag, url);
        if (Number.isFinite(duration) && duration > 0) {
          return duration;
        }
      } catch {
        // try next element type
      }
    }
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function inferMediaTags(file) {
  if (file?.type?.startsWith("video/")) return ["video"];
  if (file?.type?.startsWith("audio/")) return ["audio"];
  return ["audio", "video"];
}

function readDurationWithElement(tagName, url) {
  return new Promise((resolve, reject) => {
    const el = document.createElement(tagName);
    el.preload = "metadata";
    el.muted = true;
    el.playsInline = true;

    let timeoutId = null;
    let settled = false;

    function cleanup() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("error", onError);
      el.src = "";
    }

    function done(fn, value) {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    }

    function onLoadedMetadata() {
      const duration = el.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        done(reject, new Error("duration_unavailable"));
        return;
      }
      done(resolve, duration);
    }

    function onError() {
      done(reject, new Error("duration_read_failed"));
    }

    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("error", onError);
    timeoutId = setTimeout(
      () => done(reject, new Error("duration_read_timeout")),
      10_000,
    );
    el.src = url;
    el.load();
  });
}
